// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Validates that value is defined.
export const required = <T>(value: T | undefined, name: string) =>
    value !== undefined ? undefined : `${name} is required, but was not specified or is null/undefined.`;

export const requiredWhen = <T>(value: T | undefined, condition: boolean, name: string) =>
    condition ? required(value, name) : undefined;

// Validates an optional resource.
export const optional = <T>(value: T | undefined, func: (value: T) => string | undefined) =>
    value !== undefined ? func(value) : undefined;

// Validates a collection of resources.
export const cascade = <T>(values: T[] | undefined, func: (value: T) => string | undefined) =>
    values?.reduce<string | undefined>((result, value) => result ?? func(value), undefined);

// Validates that length of a string value is less than or equal to specified max length.
export const maxLength = (value: string | undefined, maxLength: number, name: string) =>
    value !== undefined && value.length > maxLength
        ? `${name} must be no longer than ${maxLength} characters.`
        : undefined;

// Validates that an ordinal value falls within specified [min, max] range.
export const range = <T extends number | Date>(value: T | undefined, minInclusive: T, maxInclusive: T, name: string) =>
    value !== undefined && (value < minInclusive || value > maxInclusive)
        ? `${name} must be between ${minInclusive} and ${maxInclusive} inclusive.`
        : undefined;

// Validates that value matches given regular expression.
export const regex = (value: string | undefined, regex: RegExp, message: string) =>
    value !== undefined && !regex.test(value) ? message : undefined;

// Create enum value validator given list of allowed enum values.
export const enumValidator = (values: string[]) => {
    // Justification: no user input is accepted into this regex (only used to initialize static data).
    // eslint-disable-next-line security/detect-non-literal-regexp
    const valueRegex = new RegExp(`^(${values.join('|')})$`, 'i');
    const valueList = values.length > 15 ? values.slice(0, 15).join(', ') + ', ...' : values.join(', ');
    return (value: string | undefined, name: string) =>
        regex(value, valueRegex, `${name} must be one of: ${valueList}`);
};

// Validates that value is a recent time value.
export const recentTime = (value: Date | undefined, name: string) =>
    value !== undefined && (value < new Date(2023, 1) || value > new Date())
        ? `${name} must be a recent (not future) time value.`
        : undefined;

export const requiredString = (value: unknown, name: string) =>
    typeof value === 'string' && value.length > 0 ? undefined : `${name} must be a non-empty string.`;

export const requiredBoolean = (value: unknown, name: string) =>
    typeof value === 'boolean' ? undefined : `${name} must be a boolean.`;
